import { useEffect, useState } from "react";
import dayjs, {Dayjs} from "dayjs";
import { Constant } from "app/constants/constant";
import { CoSearchModel } from "app/model/common/coModel";
import { PcAdminService } from "app/services/pc/pcAdminService";
import { ResponseResultCode } from "app/model/response-api";
import CountUp from "react-countup";
import ArcButton, { ButtonIcon, ButtonType } from "app/components/arc/ArcButton";
import { useNavigate } from "react-router-dom";
import DashboardLoading from "app/pages/db/modules/DashboardLoading";
import useInterval from "app/hooks/use-interval";

interface DbSplycsData {
  pwrUse: number;
  pwrGen: number;
  pwrEss: number;
  pwrTot: number;
  norUse: number;
  flxUse: number;
  totUse: number;
}

interface props{
  displayTime : Dayjs;
}

export default function DbSplyCs({ displayTime } : Readonly<props>) {
  const navigate = useNavigate();

  const initSplycs: DbSplycsData = {
    pwrUse: 0, pwrGen: 0, pwrEss: 0, pwrTot: 0, norUse: 0, flxUse: 0, totUse: 0
  };
  const [data, setData] = useState<DbSplycsData>(initSplycs);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    fetch();
  }, []);

  useInterval(() => {
    fetch();
  }, 600000);

  function fetch() {
    const param: Pick<CoSearchModel, 'searchType'|'fromDt'> = {
      searchType: 'DATE', fromDt: dayjs().format(Constant.FORMAT_YMD_ALT),
    };

    PcAdminService.getInstance().getSplycsStatistics(param)
    .then(response => {
      if (response && ResponseResultCode.Y === response.resultCode) {
        const supKepco: number = response.data?.kepcoPwrSupply ?? 0;
        const supPv: number = response.data?.pvPwrSupply ?? 0;
        const supEss: number = response.data?.essPwrSupply ?? 0;
        const noUse: number = response.data?.noflxFclPwrConsume ?? 0;
        const flxUse: number = response.data?.flxFclPwrConsume ?? 0;

        setData({
          pwrUse: supKepco,
          pwrGen: supPv,
          pwrEss: supEss,
          pwrTot: supKepco + supPv + supEss,
          norUse: noUse,
          flxUse: flxUse,
          totUse: noUse + flxUse,
        });
      } else {
        setData(initSplycs);
      }
    })
    .catch(() => setData(initSplycs))
    .finally(() => setIsLoading(false));
  }

  return (
    <div className="cont">
      <DashboardLoading isLoading={isLoading} />
      <h2 className="titT2 bl"><span>산단 일일 공급/소비전력</span></h2>
      <div className="area1-con">
        <p className="box-txt">
          <span>공급전력</span>
          <strong>
            <CountUp end={data.pwrTot} duration={0.5}>
              {({ countUpRef }) => <em ref={countUpRef} />}
            </CountUp>MWh
          </strong>
        </p>
        <div className="box-grid">
          <div className="box-icon">
            <div>
              <span className="icon1"></span>
              <span>한전</span>
            </div>
            <p>
              <strong>
                <CountUp end={data.pwrUse}>
                  {({ countUpRef }) => <em ref={countUpRef} />}
                </CountUp>MWh
              </strong>
            </p>
          </div>
          <div className="box-icon">
            <div>
              <span className="icon2"></span>
              <span>태양광</span>
            </div>
            <p>
              <strong>
                <CountUp end={data.pwrGen}>
                  {({ countUpRef }) => <em ref={countUpRef} />}
                </CountUp>MWh
              </strong>
            </p>
          </div>
          <div className="box-icon">
            <div>
              <span className="icon3"></span>
              <span>ESS</span>
            </div>
            <p>
              <strong>
                <CountUp end={data.pwrEss}>
                  {({ countUpRef }) => <em ref={countUpRef} />}
                </CountUp>MWh
              </strong>
            </p>
          </div>
          <div className="box-icon">
            <div>
              <span className="icon5"></span>
              <span>연료전지</span>
            </div>
            <p>
              <strong>
                <CountUp end={0}>
                  {({ countUpRef }) => <em ref={countUpRef} />}
                </CountUp>MWh
              </strong>
            </p>
          </div>
        </div>
      </div>
      <div className="area1-con mgt10">
        <p className="box-txt">
          <span>소비 전력</span>
          <strong>
            <CountUp end={data.totUse} duration={0.5}>
              {({ countUpRef }) => <em ref={countUpRef} />}
            </CountUp>MWh
          </strong>
        </p>
        <div className="box-grid">
          <div className="box-icon">
            <div>
              <span className="icon4"></span>
              <span>일반부하</span>
            </div>
            <p>
              <strong>
                <CountUp end={data.norUse}>
                  {({ countUpRef }) => <em ref={countUpRef} />}
                </CountUp>MWh
              </strong>
            </p>
          </div>
          <div className="box-icon">
            <div>
              <span className="icon4"></span>
              <span>유연부하</span>
            </div>
            <p>
              <strong>
                <CountUp end={data.flxUse}>
                  {({ countUpRef }) => <em ref={countUpRef} />}
                </CountUp>MWh
              </strong>
            </p>
          </div>
        </div>
      </div>
      <div className="mgt10">
        <p className="reference-time">기준시간 : {displayTime.format("YYYY.MM.DD HH:mm")}</p>
      </div>
      <ArcButton
        type={ButtonType.custom}
        className="more-btn"
        icon={ButtonIcon.plus}
        hasIcon
        onClick={() => navigate('/pdcs/splycs')}
      />
    </div>
  );
}